import _ from 'underscore';
import React, { useCallback, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { GlobalEventCallbackContext } from '@bingads-webui-campaign-react/global-event-callback-context';
import { useInitializeWizardPromotionalOffer, MultiCouponSelector } from '@bingads-webui-campaign-react/promotion-offers';
import { CouponContext } from '@bingads-webui-campaign-react/coupon-context';
import { usePromotionBannerContext } from '@bingads-webui-campaign-react/promotion-banner';

import { useGetCouponCurrencyCode, useBackupCoupon } from './utils';
import { useWizardCancelDialog } from './use-wizard-cancel-dialog';

export const CouponProvider = (props) => {
  const {
    children,
    isSkipWebsiteValidation,
    isLastStep,
    shouldHidePromotionBanner,
    website,
  } = props;
  const { setSignOutCallback } = useContext(GlobalEventCallbackContext);
  const { setIsHidePromotionBanner } = usePromotionBannerContext();
  const getCouponCurrencyCode = useGetCouponCurrencyCode();
  const backupCoupon = useBackupCoupon();

  const instrumentation = useContext(InstrumentationContext);

  const dialogActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.cancelDialog.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const couponActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.coupon.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  useEffect(() => {
    if (shouldHidePromotionBanner) {
      setIsHidePromotionBanner(true);
    } else {
      setIsHidePromotionBanner(false);
    }
  }, [shouldHidePromotionBanner, setIsHidePromotionBanner]);

  const [{
    coupon,
    dialogCoupon,
  }, {
    updateCoupon,
    resetCouponStore,
  }] = useInitializeWizardPromotionalOffer({
    activity: couponActivity,
    backupCoupon,
    website,
    isSignUp: true,
    isSkipWebsiteValidation,
    getCouponCurrencyCode,
  });

  const { cancelDialogResult } = useWizardCancelDialog({
    uiActivity: dialogActivity,
    dialogCoupon,
    existingCoupon: coupon,
    isLastStep,
    updateCoupon,
    getCouponCurrencyCode,
  });

  const [cancelDialog, showCancelDialog] = cancelDialogResult;

  const globalSignOutCallback = useCallback(() => {
    couponActivity.trace('Clicking MeControl sign out link to trigger cancel action in pmax lite wizard to show cancel dialog');
    showCancelDialog();
  }, [couponActivity, showCancelDialog]);

  useEffect(() => {
    setSignOutCallback(() => globalSignOutCallback);
    return () => setSignOutCallback(null);
  }, [globalSignOutCallback, setSignOutCallback]);

  return (
    <CouponContext.Provider value={{
      coupon,
      updateCoupon,
      resetCouponStore,
      isSignUp: true,
    }}
    >
      {children}
      {cancelDialog}
      <MultiCouponSelector
        activity={couponActivity}
        currentCoupon={coupon}
        updateCoupon={updateCoupon}
        getCouponCurrencyCode={getCouponCurrencyCode}
      />
    </CouponContext.Provider>
  );
};

CouponProvider.propTypes = {
  isSkipWebsiteValidation: PropTypes.bool.isRequired,
  shouldHidePromotionBanner: PropTypes.bool.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  website: PropTypes.string,
  children: PropTypes.node.isRequired,
};

CouponProvider.defaultProps = {
  website: '',
};
